<script setup>
import { WdsButtonLink, WdsRatingBreakdown, WdsReviewCard } from '@wds/components';

const props = defineProps({
  tag: {
    type: String,
    required: false,
    default: 'section',
  },
  propertyId: {
    type: Number,
    required: true,
  },
  propertyRating: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  propertyReviewsSummary: {
    type: String,
    required: false,
    default: '',
  },
  propertyLatestReviews: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const emit = defineEmits(['displayModal']);

// ### COMPOSABLES
const { $tracking } = useNuxtApp();
const { useRating } = useRatings();

// ### STORES
const testingStore = useTestingStore();

// ### A/B TESTING FEATURE TOGGLES
const experimentReviewsSummary = testingStore.hasEnabledFeature('web_enableAIReviewSummaryOnStaticPDP');

const ratingScoreTranslations = toValue(useRating?.getRatingScoreKeywordTranslations);

const ratingBreakdown = useRating?.getRatingBreakdown(props.propertyRating);

// ### REFS
const reviewsSectionRef = ref(null);
const intersectionObserver = ref(null);

// ### METHODS
function toggleReviewsPopup() {
  emit('displayModal', 'reviews');
}

function handleReviewsSectionViewed() {
  $tracking?.onTrackEvent({
    action: 'Review Section Viewed',
    sms_test_id_8: experimentReviewsSummary ? 'variation' : 'control',
    ai_review_summary_flag: props.propertyReviewsSummary !== '',
  });
}

// ### LIFECYCLE
onMounted(() => {
  if (!reviewsSectionRef.value) {
    return;
  }

  try {
    intersectionObserver.value = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleReviewsSectionViewed();
            observer?.unobserve(entry.target);
            observer?.disconnect();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      },
    );

    intersectionObserver.value.observe(reviewsSectionRef.value);
  } catch (error) {
    console.error('Failed to initialize reviews section intersection observer:', error);
  }
});

onUnmounted(() => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
    intersectionObserver.value = null;
  }
});
</script>

<template>
  <component
    :is="tag"
    ref="reviewsSectionRef"
  >
    <h2 class="rating-and-reviews-title">
      {{ $t('t_REVIEWS') }}
    </h2>
    <div class="rating-and-reviews-wrapper">
      <div class="rating-wrapper">
        <WdsRatingBreakdown
          class="rating-breakdown-container"
          :city-info="ratingBreakdown"
          :translations="ratingScoreTranslations"
          :rating-translations="ratingScoreTranslations"
          :card-layout="false"
          :enable-collapse="false"
          :is-collapsed="false"
        />
        <PropertyHwReviewsSummary
          v-if="experimentReviewsSummary"
          :property-id="propertyId"
          :property-reviews-summary="propertyReviewsSummary"
        />
      </div>
      <div class="reviews-wrapper">
        <div class="reviews-cards-list">
          <WdsReviewCard
            v-for="(review, idx) in propertyLatestReviews"
            :key="`latest-review-${idx}`"
            class="latest-review-card"
            :text="review.text"
            :stayed-in="review.date"
            :rating-score="review.rating"
            :user-info="review.author"
            :show-as-card="true"
            :truncate-text="true"
            :show-read-more="true"
            :expand-on-read-more="true"
          />
        </div>
        <WdsButtonLink
          href="#reviews"
          class="reviews-view-all mobile"
          rel="noopener"
          icon-end="chevron-right"
          :text="$t('t_VIEWREVIEWS')"
          :aria-label="$t('t_VIEWREVIEWS')"
          :title="$t('t_VIEWREVIEWS')"
          @click.prevent="toggleReviewsPopup"
        />
      </div>
    </div>
  </component>
</template>

<style scoped lang="scss">
.rating-and-reviews-wrapper {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;

  .reviews-view-all {
    cursor: pointer;

    &.mobile {
      display: flex;
      align-self: flex-end;
      margin-top: $wds-spacing-s;
    }
  }

  .rating-wrapper {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-m;

    .rating-breakdown-container {
      box-shadow: inset 0 0 0 1px $wds-color-ink-lighter;
      border-radius: $wds-border_radius-xl;
      padding: $wds-spacing-m;
    }

    :deep(.rating-breakdown-container .rating-container) {
      margin-bottom: 0;
      width: auto;
    }
  }

  .reviews-wrapper {
    display: flex;
    flex-direction: column;
    background-color: $wds-color-sunset-light;
    padding: wds-rem(40px) 0 $wds-spacing-m;
    margin-inline: -$wds-spacing-m;
    margin-top: $wds-spacing-s;
    clip-path: polygon(0 5%, 100% 0, 100% 100%, 0 95%);
    min-width: 0;

    .reviews-cards-list {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: $wds-spacing-m;
      width: 100%;
      overflow-x: auto;
      padding-inline: $wds-spacing-m;

      .latest-review-card {
        min-width: wds-rem(288px);
        min-height: auto;
      }
    }
  }

  @include tablet {
    .reviews-wrapper {
      margin-inline: -$wds-spacing-l;
    }
  }

  @include tablet-large {
    .reviews-wrapper {
      margin-inline: 0;
    }
  }

  @include desktop {
    min-height: wds-rem(415px);
    flex-direction: row;
    gap: $wds-spacing-l;

    // padding-inline: $wds-spacing-l;

    .reviews-view-all {
      &.mobile {
        display: flex;
        margin-top: $wds-spacing-m;
      }
    }

    .rating-wrapper {
      flex: 0 0 wds-rem(288px);
      padding-inline: 0;

      :deep(.rating-breakdown-container .rating-container) {
        width: auto;
      }
    }

    .reviews-wrapper {
      flex: 1;
      flex-direction: column;
      background-color: $wds-color-sunset-light;
      border-radius: wds-rem(40px);
      padding: $wds-spacing-l;
      clip-path: initial;

      .reviews-cards-list {
        padding-inline: 0;
        flex-direction: column;

        .latest-review-card {
          width: 100%;
          min-width: wds-rem(210px);
        }
      }
    }
  }

  @include desktop-large {
    .rating-wrapper {
      flex-basis: wds-rem(360px);
    }
  }
}
</style>
